import Grid from '@material-ui/core/Grid';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';

import type { IBanklist, INewAccount, IOperationList } from 'v2/interfaces/financial';
import type { IProfile } from 'v2/interfaces/profile';

import BankReceipt from './BankReceipt';
import { newAccount } from './formik';
import NavButtons from './NavButtons';
import NewAccountForm from './NewAccountForm';
import PersonalDocuments from './PersonalDocuments';
import Resume from './Resume';
import { useStyles } from './styles';
import { getSchema } from './validation';

interface IProps {
  profile?: IProfile;
  banks: IBanklist[];
  operations: IOperationList[];
  isForeign?: boolean;
  isLoadingBanks: boolean;
  isLoadingOperations: boolean;
  hasVideo?: boolean;
  isRefundAccount?: boolean;
  onSubmit: (values: INewAccount) => void;
}

const createAccountSteps = ['Conta', 'Documentos', 'Comprovante Bancário', 'Resumo'];

const CreateBankAccountStepper = ({ profile, isForeign = false, hasVideo = true, onSubmit, ...props }: IProps) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();

  const [docType, setDocType] = useState('CNH');
  const [activeStep, setActiveStep] = useState(0);
  const [mediaPreview, setMediaPreview] = useState({ imgDocCNH: '', imgDocCPF: '', imgDocRG: '', imgBankReceipt: '' });

  const initialValues: INewAccount = {
    ...newAccount,
    document_number: profile?.document_number || '',
    name: profile?.name || '',
  };

  const toggleDocType = () => {
    setDocType(docType === 'CNH' ? 'RG/CPF' : 'CNH');
  };

  const handleMediaPreview = (name: string, preview: any) => {
    setMediaPreview({
      ...mediaPreview,
      [name]: preview,
    });
  };

  const getStepContent = (activeStep: number) => {
    const screenSteps = {
      0: <NewAccountForm {...props} isForeign={isForeign} />,
      1: (
        <PersonalDocuments
          hasVideo={hasVideo}
          docType={docType}
          mediaPreview={mediaPreview}
          onChangeDocType={toggleDocType}
          setMediaPreview={handleMediaPreview}
        />
      ),
      2: <BankReceipt mediaPreview={mediaPreview} setMediaPreview={handleMediaPreview} />,
      3: <Resume />,
    };

    return screenSteps[activeStep];
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const newAccountSchema = getSchema(isForeign);

  return (
    <Formik validationSchema={newAccountSchema} initialValues={initialValues} onSubmit={onSubmit}>
      <Form>
        <Stepper activeStep={activeStep} className={classes.stepper} orientation={mobile ? 'vertical' : 'horizontal'}>
          {createAccountSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}

        <div className='m16' />
        <Grid container alignItems='center' justify='space-between'>
          <NavButtons activeStep={activeStep} hasVideo={hasVideo} docType={docType} onBack={handleBack} onNext={handleNext} />
        </Grid>
      </Form>
    </Formik>
  );
};

export default CreateBankAccountStepper;
